import * as React from 'react';
import {
  TimeField,
  MultiSectionDigitalClock,
  LocalizationProvider,
} from '@mui/x-date-pickers-pro';
import {
  Box,
  Button,
  Paper,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import { format, setHours, setMinutes } from 'date-fns';
import { cx } from '../../../helpers/global';

type Props = {
  onChange: (value: Date) => void;
  closeModal: () => void;
  selectedDay: Date;
  /** At a multi-select cap there is no room for another time */
  disabled?: boolean;
};

const MINUTE_STEP = 5;
// nicer than MUI's default "aa"
const MERIDIEM_PLACEHOLDER = 'AM';

const snapToStep = (value: Date): Date => {
  const total = value.getHours() * 60 + value.getMinutes();
  const snapped = (Math.round(total / MINUTE_STEP) * MINUTE_STEP) % 1440;
  return setMinutes(setHours(value, Math.floor(snapped / 60)), snapped % 60);
};

export default function CustomTime({
  onChange,
  closeModal,
  selectedDay,
  disabled = false,
}: Props) {
  const theme = useTheme();
  const [showCustomTime, setShowCustomTime] = React.useState(false);
  const [startTime, setStartTime] = React.useState<Date | null>(null);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const applyTime = (datetime: Date | null) => {
    if (!datetime || Number.isNaN(datetime.getTime())) {
      return;
    }

    const snapped = snapToStep(datetime);

    onChange(
      setMinutes(
        setHours(selectedDay, snapped.getHours()),
        snapped.getMinutes(),
      ),
    );
    setShowCustomTime(false);
    setStartTime(null);
    closeModal();
  };

  const dialValue =
    startTime && !Number.isNaN(startTime.getTime())
      ? snapToStep(startTime)
      : null;

  if (!showCustomTime) {
    return (
      <div
        role="presentation"
        className={cx(
          'time-item',
          startTime && 'selected',
          disabled && 'disabled',
        )}
        onClick={() => {
          if (disabled) return;
          setShowCustomTime(true);
          setStartTime(null);
        }}>
        <Typography variant="h3">
          {startTime ? format(startTime, 'h:mma') : 'Custom time'}
        </Typography>
      </div>
    );
  }

  return (
    <Box ref={setAnchorEl}>
      <LocalizationProvider
        localeText={{ fieldMeridiemPlaceholder: () => MERIDIEM_PLACEHOLDER }}>
        <TimeField
          label="Custom time"
          value={startTime}
          onChange={value =>
            setStartTime(value && !Number.isNaN(value.getTime()) ? value : null)
          }
          onBlur={() => {
            if (dialValue) {
              setStartTime(dialValue);
            }
          }}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              applyTime(startTime);
            }
          }}
          autoFocus
          fullWidth
        />
      </LocalizationProvider>

      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        sx={{ zIndex: theme.zIndex.modal + 1 }}>
        <Paper elevation={6} sx={{ mt: 0.5 }}>
          <MultiSectionDigitalClock
            value={dialValue}
            onChange={value => setStartTime(value)}
            timeSteps={{ minutes: MINUTE_STEP }}
          />
          <Box
            p={1}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}>
            <Button
              type="button"
              variant="light"
              onClick={() => {
                setShowCustomTime(false);
                setStartTime(null);
              }}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="base"
              disabled={!startTime}
              onClick={() => applyTime(startTime)}>
              OK
            </Button>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
}
